import React, { FC, useMemo, memo } from "react";
import Snackbar from "material-ui/Snackbar";
import LatestSnackbar, { SnackbarOrigin } from "@material-ui/core/Snackbar";
import { DATATEST } from "constants/integration";
import { List } from "immutable";
import { ClickAwayListenerProps, makeStyles } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { ContentLoading } from "components/content-loading";
import { Link } from "react-router";
import CheckCircleOutlineOutlinedIcon from "@material-ui/icons/CheckCircleOutlineOutlined";
import { InsightsIcon } from "pages/smart-orders/components/icons/insights-icon";
import InsightsAddAction from "../insights/insights-snackbar-add-action";
import type { ICustomSnackbarAction } from "types/working-order/line-items";

interface ISnackbar {
    id: string;
    message: string;
    position: SnackbarOrigin;
    displayLoader?: boolean;
    action?: React.ReactNode;
    options?: any;
    preventClickAway?: boolean;
    shouldUseLatestSnack?: boolean;
    autoHideDuration?: number;
    messageStyle?: { [key: string]: string | number };
    onActionClick?: React.MouseEventHandler<{}>;
    testId?: string;
    type?: string;
    customAction?: ICustomSnackbarAction;
}

interface IProps {
    snackbars: List<ISnackbar>;
    onClearSnackbar: () => void;
}

const Snackbars: FC<IProps> = (props: IProps) => {
    const { snackbars, onClearSnackbar } = props;

    const current = snackbars.first();
    const styles = useMemo(() => {
        const { options = {} } = current || {};

        if (current?.type === "replenishment") {
            options.bodyStyle = { ...options.bodyStyle, left: undefined };
            options.messageContainer = {
                ...options.messageContainer,
                width: "100%",
                display: "flex",
                lineHeight: undefined,
            };
        }

        return {
            style: { ...(options.style || {}) },
            bodyStyle: { zIndex: 1600, maxWidth: "inherit", whiteSpace: "nowrap", ...(options.bodyStyle || {}) },
            messageContainer: current?.displayLoader
                ? { width: "100%", display: "flex", lineHeight: "30px" }
                : { ...(options.messageContainer || {}) },
            loaderStyle: { display: "inline", marginRight: "10px" },
        };
    }, [current]);
    const clickAwayProps = useMemo(
        () => (current?.preventClickAway ? ({ mouseEvent: false, touchEvent: false } as ClickAwayListenerProps) : {}),
        [current]
    );

    const classes = useStyles();

    if (current?.type === "replenishment") {
        current.position = { vertical: "bottom", horizontal: "center" };
    }

    return current?.shouldUseLatestSnack ? (
        <LatestSnackbar
            style={styles.bodyStyle}
            classes={current?.type === "replenishment" ? { root: classes.root } : {}}
            anchorOrigin={current?.position}
            open={!!current}
            ClickAwayListenerProps={clickAwayProps}
            disableWindowBlurListener={!!current?.preventClickAway}
            autoHideDuration={current?.autoHideDuration !== undefined ? current?.autoHideDuration : 6000}
            message={
                current ? (
                    <div style={styles.messageContainer}>
                        {current.displayLoader && (
                            <span style={styles.loaderStyle}>
                                <ContentLoading
                                    data-test={DATATEST.SNACKBAR_LOADER}
                                    className="toastContentLoader"
                                    size={30}
                                />
                            </span>
                        )}
                        {current?.type === "replenishment" && <InsightsIcon />}
                        <span
                            data-test={DATATEST.SNACKBAR_MESSAGE}
                            style={current?.messageStyle}
                            className="snackbar-message"
                        >
                            {current?.message}
                        </span>
                    </div>
                ) : (
                    <div />
                )
            }
            onClose={onClearSnackbar}
            action={
                current?.customAction?.type === "insights-add" ? (
                    <InsightsAddAction
                        data-test={DATATEST.SNACKBAR_CUSTOM_ACTIONS.INSIGHTS_ADD}
                        id={current.id}
                        brandId={current.customAction.data.brandId}
                        itemId={current.customAction.data.itemId}
                    />
                ) : (
                    current?.action
                )
            }
            data-test={DATATEST.SNACKBAR_LATEST}
        />
    ) : current?.options?.open ? (
        <Snackbar
            open={!!current}
            message={
                current ? (
                    <div style={styles.messageContainer}>
                        {current.displayLoader && (
                            <span style={styles.loaderStyle}>
                                <ContentLoading
                                    data-test={DATATEST.SNACKBAR_LOADER}
                                    className="toastContentLoader"
                                    size={30}
                                />
                            </span>
                        )}
                        {current.options?.b2bPhase1 && (
                            <span>
                                <CheckCircleOutlineOutlinedIcon
                                    className="snackbarCheckIcon"
                                    style={{ fontSize: "23px", color: "#4AA06B", margin: "0 10px -6px 0" }}
                                />
                            </span>
                        )}
                        <span data-test={DATATEST.SNACKBAR_MESSAGE} className="snackbar-message">
                            {current.message}
                        </span>
                        {current.options?.url && (
                            <Link
                                onClick={current.options?.onClick}
                                key={current.options?.key}
                                to={current.options?.url}
                                target="_blank"
                                style={{ position: "relative", top: "-1px", marginLeft: "15px", cursor: "pointer" }}
                            >
                                {current.options?.title}
                            </Link>
                        )}
                        <CloseIcon
                            data-test={DATATEST.SNACKBAR_CLOSE}
                            onClick={onClearSnackbar}
                            key="close-snack"
                            color="inherit"
                            style={{
                                color: "white",
                                margin: current.options?.b2bPhase1 ? "-3px 0px -5px 19px" : "-3px 0px 0px 19px",
                                fontSize: "20px",
                                cursor: "pointer",
                            }}
                        />
                    </div>
                ) : (
                    <div />
                )
            }
            action={current && current.action}
            onActionClick={current && current.onActionClick}
            bodyStyle={styles.bodyStyle}
            style={styles.style}
            onRequestClose={(reason) => {
                if (reason === "clickaway") {
                    return;
                }
            }}
            data-test={DATATEST.SNACKBAR_OLD_VERSION}
        />
    ) : (
        <Snackbar
            open={!!current}
            message={
                current ? (
                    <span style={styles.messageContainer}>
                        {current.displayLoader && (
                            <span style={styles.loaderStyle}>
                                <ContentLoading
                                    data-test={DATATEST.SNACKBAR_LOADER}
                                    className="toastContentLoader"
                                    size={30}
                                />
                            </span>
                        )}
                        <span data-test={current?.testId || DATATEST.SNACKBAR_MESSAGE} className="snackbar-message">
                            {current?.message}
                        </span>
                    </span>
                ) : (
                    <span />
                )
            }
            autoHideDuration={current?.autoHideDuration !== undefined ? current?.autoHideDuration : 6000}
            action={current?.action}
            onActionClick={current?.onActionClick}
            onRequestClose={onClearSnackbar}
            bodyStyle={styles.bodyStyle}
            style={styles.style}
            data-test={DATATEST.SNACKBAR_OLD_VERSION}
        />
    );
};

const useStyles = makeStyles(() => ({
    root: {
        "& .MuiSnackbarContent-root": {
            backgroundColor: "#F8EEFC",
            color: "#3F3F3F",
            flexWrap: "nowrap",
        },
    },
}));

export default memo(Snackbars, (prevProps, nextProps) => prevProps.snackbars === nextProps.snackbars);
